import styled from '@emotion/styled';
import { filterNullish } from '@modernhealth/filter-nullish';
import { colors } from '@modernhealth/vital-foundation';
import { ComponentTextXSmall, pxToRem } from '@modernhealth/vital-components';
import React, { FC } from 'react';
import groupBy from 'lodash/groupBy';

import {
    ConversationNode,
    MessageNode,
    MessageResourceNode,
} from 'src/shared/messaging-kit/documents/messaging_gql.generated';

import { MessageContent } from './MessageContent';

// A resource only exists if has an id
const existsResource = (resource: MessageResourceNode) => {
    return resource.meditationId || resource.programId || resource.companyAdditionalBenefitId;
};

interface MessagesListProps {
    messages: readonly Readonly<MessageNode>[];
    conversation: Readonly<ConversationNode> | undefined;
}

export const MessagesList: FC<MessagesListProps> = ({ messages, conversation }) => {
    if (!conversation || messages.length < 1) {
        return null;
    }

    const groupedMessages: Record<string, MessagesListProps['messages']> = groupBy(
        messages,
        node => {
            const d = new Date(node.sentAt);
            return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
        },
    );

    return (
        <>
            {Object.keys(groupedMessages).map(key => {
                const sentAtDateTime = new Date(key);

                return (
                    <StyledMessageDateGroupContainer data-testid={key} key={key}>
                        {groupedMessages[key].map((m: MessageNode) => {
                            const sender =
                                conversation && conversation.member?.uuid === m.senderUuid
                                    ? conversation?.member
                                    : conversation?.coach;
                            const messageResources = !!m.messageResources
                                ? filterNullish(m.messageResources)
                                : undefined;

                            // We allow HR managers to remove benefits, so we may have messages that
                            // contains resources that no longer exists, so we need to ensure that the
                            // resources contain at least one valid resource
                            const hasAValidResource = messageResources?.some(r =>
                                existsResource(r),
                            );

                            // we should only show the message if a valid resource is used or has text
                            if (m.content === '' && !hasAValidResource) {
                                return null;
                            }

                            return (
                                <MessageContent
                                    key={m.uuid}
                                    sender={sender}
                                    content={m.content}
                                    messageResources={messageResources}
                                    sentAt={m.sentAt}
                                />
                            );
                        })}
                        <StyledGroupDateContainer>
                            <StyledGroupDateDiv>
                                <StyledGroupDate fontColor="gray55">
                                    {sentAtDateTime.toLocaleString([], {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </StyledGroupDate>
                            </StyledGroupDateDiv>
                        </StyledGroupDateContainer>
                        <StyleGroupDateLineContainer>
                            <StyleGroupDateLine />
                        </StyleGroupDateLineContainer>
                    </StyledMessageDateGroupContainer>
                );
            })}
        </>
    );
};

const DATE_HEIGHT = 46;
const DATE_MARGIN = 18;
const DATE_HR_HEIGHT = 2;

const StyledMessageDateGroupContainer = styled.div`
    display: flex;
    flex-flow: column-reverse;
`;

const StyledGroupDateContainer = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledGroupDateDiv = styled.div`
    margin: ${pxToRem(DATE_MARGIN / 2, 0)};
    height: ${pxToRem(DATE_HEIGHT - DATE_MARGIN)};
    display: flex;
    align-items: center;
    background-color: white;
    border: 2px solid ${colors.gray05};
    border-radius: ${pxToRem(DATE_HEIGHT / 2)};
`;

const StyledGroupDate = styled(ComponentTextXSmall)`
    padding: ${pxToRem(0, 10)};
    font-weight: 500;
`;

const StyleGroupDateLineContainer = styled.div`
    position: relative;
    width: 100%;
    height: 0;
`;

const StyleGroupDateLine = styled.div`
    height: ${pxToRem(DATE_HR_HEIGHT)};
    background-color: ${colors.gray05};
    width: 100%;
    position: absolute;
    top: ${pxToRem((DATE_HEIGHT - DATE_HR_HEIGHT) / 2)};
`;
